import type { SwitchboardFlowData } from '../converters/shared/reactFlowTypes'

const SAVED_SNAPSHOT_STORAGE_KEY = 'switchboard-saved-snapshot-v2'
const SAVED_SNAPSHOT_TTL_MS = 2 * 60 * 1000

export interface SavedSwitchboardSnapshot {
  switchboardId: number
  flowData: SwitchboardFlowData
  contentSignature: string
  savedAt: number
  updatedAt?: string
}

function isUsableSnapshot(
  snapshot: SavedSwitchboardSnapshot,
  switchboardId: number,
  now = Date.now(),
): boolean {
  return (
    snapshot.switchboardId === switchboardId &&
    Array.isArray(snapshot.flowData?.nodes) &&
    Array.isArray(snapshot.flowData?.edges) &&
    Number.isFinite(snapshot.savedAt) &&
    now - snapshot.savedAt <= SAVED_SNAPSHOT_TTL_MS
  )
}

export function isSavedSwitchboardSnapshotUsable(
  snapshot: SavedSwitchboardSnapshot,
  switchboardId: number,
): boolean {
  return isUsableSnapshot(snapshot, switchboardId)
}

function parseTimestamp(value?: string): number | null {
  if (!value) return null

  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function isSavedSwitchboardSnapshotOlderThanServer(
  snapshot: SavedSwitchboardSnapshot,
  serverUpdatedAt?: string,
): boolean {
  const snapshotMs = parseTimestamp(snapshot.updatedAt)
  const serverMs = parseTimestamp(serverUpdatedAt)

  return snapshotMs !== null && serverMs !== null && serverMs > snapshotMs
}

export function getSavedSwitchboardSnapshotFromStorage(
  switchboardId: number,
  serverUpdatedAt?: string,
): SavedSwitchboardSnapshot | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = sessionStorage.getItem(SAVED_SNAPSHOT_STORAGE_KEY)
    if (!stored) return null

    const snapshot = JSON.parse(stored) as SavedSwitchboardSnapshot
    if (!isUsableSnapshot(snapshot, switchboardId)) {
      sessionStorage.removeItem(SAVED_SNAPSHOT_STORAGE_KEY)
      return null
    }

    if (isSavedSwitchboardSnapshotOlderThanServer(snapshot, serverUpdatedAt)) {
      sessionStorage.removeItem(SAVED_SNAPSHOT_STORAGE_KEY)
      return null
    }

    return snapshot
  } catch (e) {
    console.error('Failed to read switchboard saved snapshot:', e)
    // Remove the unparseable entry so a single corrupted value cannot keep
    // failing (and logging) on every subsequent read.
    try {
      sessionStorage.removeItem(SAVED_SNAPSHOT_STORAGE_KEY)
    } catch {
      // Ignore: storage is already unavailable/broken.
    }
    return null
  }
}

export function saveSwitchboardSnapshotToStorage(
  snapshot: SavedSwitchboardSnapshot,
): void {
  if (typeof window === 'undefined') return

  try {
    sessionStorage.setItem(SAVED_SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshot))
  } catch (e) {
    console.error('Failed to save switchboard saved snapshot:', e)
  }
}

export function clearSavedSwitchboardSnapshotFromStorage(
  switchboardId?: number,
): void {
  if (typeof window === 'undefined') return

  try {
    if (switchboardId !== undefined) {
      const stored = sessionStorage.getItem(SAVED_SNAPSHOT_STORAGE_KEY)
      if (stored) {
        const snapshot = JSON.parse(stored) as SavedSwitchboardSnapshot
        if (snapshot.switchboardId !== switchboardId) return
      }
    }

    sessionStorage.removeItem(SAVED_SNAPSHOT_STORAGE_KEY)
  } catch (e) {
    console.error('Failed to clear switchboard saved snapshot:', e)
  }
}
